import { useMemo } from 'react';
import { Section, Bullet } from './ReportUIPrimitives';
import styles from './MonthlyIdeationViewer.module.css';

const fmtDate = (value = '') => {
  try {
    const date = new Date(value);
    if (isNaN(date.getTime())) return String(value || '—');
    return date.toLocaleDateString(undefined, {
      month: 'short',
      day: '2-digit',
      year: 'numeric',
    });
  } catch {
    return String(value || '—');
  }
};

const IdeaCard = ({ item = {} }) => (
  <div className={styles.ideaCard}>
    <h5>{item.title || 'Untitled idea'}</h5>
    {item.angle ? <Bullet>Angle: {item.angle}</Bullet> : null}
    {item.copy_direction ? <Bullet>Copy direction: {item.copy_direction}</Bullet> : null}
    {item.notes ? <Bullet>Notes: {item.notes}</Bullet> : null}
  </div>
);

const EventCard = ({ entry = {} }) => (
  <div className={styles.eventCard}>
    <span className={styles.badge}>{(entry.channel || 'event').toUpperCase()}</span>
    <h5>{entry.event || entry.name || 'Unnamed event'}</h5>
    <span className={styles.meta}>{fmtDate(entry.date)}</span>
    <p>Channel: {entry.channel || '—'}</p>
    {entry.angle ? <p>Angle: {entry.angle}</p> : null}
  </div>
);

const CalendarCard = ({ entry = {} }) => (
  <div className={styles.calendarCard}>
    <h5>{fmtDate(entry.date)}</h5>
    <span className={styles.meta}>Event: {entry.event || entry.name || '—'}</span>
    {Array.isArray(entry.content) && entry.content.length ? (
      <div className={styles.calendarList}>
        {entry.content.map((item, idx) => (
          <p key={idx}>
            {item.type}: {item.title}
          </p>
        ))}
      </div>
    ) : entry.content ? (
      <p>{entry.content}</p>
    ) : null}
  </div>
);

const MonthlyIdeationViewer = ({ data = {} }) => {
  const ideation =
    data?.ideation ||
    data?.data?.ideation ||
    data?.ideation?.ideation ||
    data?.data?.ideation?.ideation ||
    {};
  const eventMap = Array.isArray(ideation.event_map) ? ideation.event_map : [];
  const calendar = Array.isArray(ideation.calendar) ? ideation.calendar : [];
  const ideas = {
    ad_creatives: Array.isArray(ideation.ideas?.ad_creatives)
      ? ideation.ideas.ad_creatives
      : [],
    social: Array.isArray(ideation.ideas?.social) ? ideation.ideas.social : [],
    blog_seo: Array.isArray(ideation.ideas?.blog_seo)
      ? ideation.ideas.blog_seo
      : [],
    email: Array.isArray(ideation.ideas?.email) ? ideation.ideas.email : [],
  };
  const weekly = ideation?.cadence?.weekly || {};

  const ideaGroups = useMemo(
    () => [
      { title: 'Ad Creatives', items: ideas.ad_creatives },
      { title: 'Social', items: ideas.social },
      { title: 'Blog (SEO)', items: ideas.blog_seo },
      { title: 'Email', items: ideas.email },
    ],
    [ideas]
  );

  return (
    <div className={styles.container}>
      {eventMap.length ? (
        <Section title="Event Map">
          <div className={styles.cardGrid}>
            {eventMap.map((entry, idx) => (
              <EventCard key={`map-${idx}`} entry={entry} />
            ))}
          </div>
        </Section>
      ) : null}

      <Section title="Weekly Cadence">
        <div className={styles.cadenceGrid}>
          <div className={styles.cadenceCell}>
            <span>Ads</span>
            <strong>{weekly.ads ?? '—'}</strong>
          </div>
          <div className={styles.cadenceCell}>
            <span>Social</span>
            <strong>{weekly.social_posts ?? '—'}</strong>
          </div>
          <div className={styles.cadenceCell}>
            <span>Emails</span>
            <strong>{weekly.emails ?? '—'}</strong>
          </div>
          <div className={styles.cadenceCell}>
            <span>Blogs</span>
            <strong>{weekly.blogs ?? '—'}</strong>
          </div>
        </div>
      </Section>

      {ideaGroups.map((group) =>
        group.items.length ? (
          <Section title={group.title} key={group.title}>
            <div className={styles.cardGrid}>
              {group.items.map((item, idx) => (
                <IdeaCard key={`${group.title}-${idx}`} item={item} />
              ))}
            </div>
          </Section>
        ) : null
      )}

      {calendar.length ? (
        <Section title="Content Calendar">
          <div className={styles.cardGrid}>
            {calendar.map((entry, idx) => (
              <CalendarCard key={`cal-${idx}`} entry={entry} />
            ))}
          </div>
        </Section>
      ) : null}
    </div>
  );
};

export default MonthlyIdeationViewer;
